// The floating bulk-action bar: Buttons' BulkActionToolbar in the page's FloatingBarStack, whose content
// region is this table unit, so the bar spans only the column the table sits in and never crosses the
// table of contents, the tool panel or the icon bar beside it. It appears with the first selection, offers
// the eligible matching set once a page is fully selected, runs one bulk action at a time against a
// snapshot, and reports each outcome in the unit's status line. The bar rides above the proxy scrollbar.

import React, { useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import {
  BulkActionToolbar,
  Button,
  FloatingBarStack,
  InlineConfirm,
} from '@CommonComponents/buttons'
import { useTable } from '../context'
import { iconClass } from '../cells/menuItems'
import {
  CANCEL,
  FAILURE_TEXT,
  allSelectedLabel,
  confirmQuestion,
  doneText,
  escalateLabel,
  selectedLabel,
  workingLabel,
} from './copy'
import { useSelection } from './useSelection'

const isText = value => typeof value === 'string' && value.trim() !== ''

// controls whose Escape belongs to the person's editing, not to the selection
const EDITABLE =
  'textarea, select, [contenteditable=""], [contenteditable="true"], ' +
  'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"])'

const isEditable = target =>
  Boolean(target && target.closest && target.closest(EDITABLE))

const tryAgain = () =>
  (window.MEButtons && window.MEButtons.text('strTryAgain')) || 'Try again'

const stackFor = region =>
  Array.from(document.querySelectorAll('.me-floating-bar-stack')).find(
    stack => stack.dataset.contentRegion === region
  )

// the stack lifts by the proxy scrollbar's height, so the bar rides above it
const liftStack = (region, lift) => {
  const stack = stackFor(region)
  if (!stack) return
  stack.setAttribute('data-me-table-bulk', '')
  stack.style.setProperty('--me-table-proxy-lift', `${lift}px`)
}

const actionClass = action =>
  action.primary || action.treatment === 'primary'
    ? 'me-btn me-btn-primary me-btn-sm'
    : 'me-btn me-btn-secondary me-btn-sm'

/** One bulk action: a destination, a command, or a destructive command that confirms with the count. */
const bulkControl = (action, { count, pending, run }) => {
  const icon = iconClass(action.icon)
  const blocked = Boolean(pending) || undefined
  if (isText(action.href) && typeof action.onSelect !== 'function')
    return (
      <Button
        key={action.key}
        href={action.href}
        className={actionClass(action)}
        icon={icon}
        aria-disabled={blocked}
      >
        {action.label}
      </Button>
    )
  if (action.destructive)
    return (
      <InlineConfirm
        key={action.key}
        className="me-btn me-btn-secondary me-btn-sm"
        aria-disabled={blocked}
        data-inline-confirm={confirmQuestion(action.label, count)}
        data-inline-verb={action.label}
        data-inline-cancel={CANCEL}
        data-inline-done={doneText(action.label, count)}
        data-busy-label={workingLabel(count)}
        data-failure-label={FAILURE_TEXT}
        data-failure-text={tryAgain()}
        onConfirm={() => run(action)}
      >
        {action.label}
      </InlineConfirm>
    )
  return (
    <Button
      key={action.key}
      className={actionClass(action)}
      icon={icon}
      aria-disabled={blocked}
      onClick={() => run(action).catch(() => {})}
    >
      {action.label}
    </Button>
  )
}

const SelectionBar = ({ actions }) => {
  const table = useTable()
  const selection = useSelection()
  const { pending, count, queryScoped, eligibleTotal } = selection
  const region = table.unitId
  const suppress = useRef(false)
  const busy = useRef(false)
  busy.current = Boolean(pending)

  // Escape typed into an editable control, or while work runs, never clears the selection
  useEffect(() => {
    let timer
    const capture = event => {
      if (event.key !== 'Escape') return
      if (!busy.current && !isEditable(event.target)) return
      suppress.current = true
      clearTimeout(timer)
      timer = setTimeout(() => {
        suppress.current = false
      })
    }
    document.addEventListener('keydown', capture, true)
    return () => {
      document.removeEventListener('keydown', capture, true)
      clearTimeout(timer)
    }
  }, [])

  const showing = pending ? pending.count : count
  let label = selectedLabel(showing)
  if (pending) label = workingLabel(pending.count)
  else if (queryScoped) label = allSelectedLabel(count)
  const lift = (table.geometry && table.geometry.proxyHeight) || 0

  useLayoutEffect(() => {
    if (region) liftStack(region, lift)
  })

  // the unit marks active selection, which keeps every row checkbox visible
  const selecting = selection.active || Boolean(pending)
  useLayoutEffect(() => {
    const unit = table.refs && table.refs.unit && table.refs.unit.current
    if (unit) unit.toggleAttribute('data-me-selecting', selecting)
  }, [table.refs, selecting])

  const onClear = () => {
    if (suppress.current || busy.current) return
    selection.clear()
  }

  const controls = []
  if (selection.canEscalate && !pending)
    controls.push(
      <Button
        key="me-table-select-all-matching"
        className="me-btn me-btn-ghost me-btn-sm"
        onClick={selection.escalate}
      >
        {escalateLabel(eligibleTotal)}
      </Button>
    )
  actions.forEach(action =>
    controls.push(
      bulkControl(action, { count: showing, pending, run: selection.run })
    )
  )

  return (
    <>
      <div className="me-table-bulk-status" role="status">
        {selection.notice ? selection.notice.text : ''}
      </div>
      {region && (
        <FloatingBarStack contentId={region}>
          <BulkActionToolbar
            count={showing}
            label={label}
            busy={Boolean(pending)}
            onClear={onClear}
          >
            {controls}
          </BulkActionToolbar>
        </FloatingBarStack>
      )}
    </>
  )
}

SelectionBar.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export const BulkBar = () => {
  const { layout, config } = useTable()
  if (!layout.selectable || !config.selection) return null
  const actions = (config.selection.bulkActions || []).filter(
    action => action && isText(action.key) && isText(action.label)
  )
  return <SelectionBar actions={actions} />
}
